import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type {
  Filters,
  GraphIntentVerificationBadge,
  PathChecksBadgeRequestBody,
  PathChecksBadgeResponseBody,
  PathLookupCheck,
  PathLookupChecksResponseBody,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import type { Pagination } from 'utils/types/Pagination';
import type { PathChecksFormValues } from './GraphPathChecksFilter';

export interface FetchPathLookupChecksCommand {
  filters: PathChecksFormValues | null;
  pagination: Pagination;
}

export interface DeletePathLookupCheckCommand {
  id: string;
}

const columns = [
  'id',
  'dst',
  'dstPorts',
  'expectedPassingTraffic',
  'flags',
  'networkMode',
  'protocol',
  'receiver',
  'securedPath',
  'src',
  'srcPorts',
  'parameters',
  'passingTraffic',
  'settings',
  'type',
  'vrf',
];

const getFilters = (values: PathChecksFormValues | null): Filters => {
  if (values === null) {
    return {};
  }

  const filters: Filters = {};

  if (values.src) {
    filters.src = ['like', values.src];
  }

  if (values.dst) {
    filters.dst = ['like', values.dst];
  }

  if (values.ok && !values.fail) {
    filters.expectedPassingTraffic = ['eq', 'all'];
  }

  if (values.fail && !values.ok) {
    filters.expectedPassingTraffic = ['eq', 'none'];
  }

  if (values.unicast && !values.multicast) {
    filters.type = ['eq', 'unicast'];
  }

  if (values.multicast && !values.unicast) {
    filters.type = ['eq', 'multicast'];
  }

  return filters;
};

interface PathChecksList {
  count: number | null;
  items: PathLookupCheck[];
}

export interface PathChecksHook {
  badge?: GraphIntentVerificationBadge;
  currentFilters: PathChecksFormValues | null;
  deletePathLookupCheck: (command: DeletePathLookupCheckCommand) => void;
  fetchBadge: () => void;
  fetchPathLookupChecks: (command: FetchPathLookupChecksCommand) => void;
  list: PathChecksList;
  listLoadingState: LoadingState;
  loading: boolean;
}

export const usePathChecks = (): PathChecksHook => {
  const apiRequest = useApi().request;
  const [badgeLoadingState, setBadgeLoadingState] = useLoadingState();
  const [listLoadingState, setListLoadingState] = useLoadingState();
  const [badge, setBadge] = useState<
    GraphIntentVerificationBadge | undefined
  >();
  const [currentFilters, setCurrentFilters] =
    useState<PathChecksFormValues | null>(null);
  const [list, setList] = useState<PathChecksList>({ count: null, items: [] });
  const { showNotification } = useNotifications();
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    snapshotId: prop('snapshotId'),
  });

  const fetchBadge = async () => {
    const badgeResponse = await withLoadingIndicator(setBadgeLoadingState)(() =>
      apiRequest<PathChecksBadgeResponseBody, PathChecksBadgeRequestBody>({
        url: '/graphs/path-lookup/checks/badge',
        method: 'POST',
        data: {
          snapshot: ctxProps.snapshotId,
        },
      }),
    );

    setBadge(badgeResponse);
  };

  const fetchPathLookupChecks = async (
    command: FetchPathLookupChecksCommand,
  ) => {
    const response = await withLoadingIndicator((state) => {
      if (state === 'LOADING' && command.pagination.start === 0) {
        setListLoadingState('LOADING');
      } else if (state !== 'LOADING') {
        setListLoadingState(state);
      }
    })(() =>
      apiRequest<PathLookupChecksResponseBody>({
        url: '/tables/networks/path-lookup-checks',
        method: 'POST',
        data: {
          columns,
          filters: getFilters(command.filters),
          snapshot: ctxProps.snapshotId,
          pagination: command.pagination,
        },
      }),
    );

    setCurrentFilters(command.filters);

    if (command.pagination.start === 0) {
      setList({
        count: response._meta.count,
        items: response.data,
      });
    } else {
      setList({
        count: response._meta.count,
        items: [...list.items, ...response.data],
      });
    }
  };

  const deletePathLookupCheck = async (
    command: DeletePathLookupCheckCommand,
  ) => {
    if (list.count === null) {
      return;
    }

    await apiRequest({
      url: `/graphs/path-lookup/checks/${encodeURIComponent(command.id)}`,
      method: 'DELETE',
    });

    setList({
      count: list.count - 1,
      items: list.items.filter(({ id }) => id !== command.id),
    });

    showNotification({
      type: 'success',
      message: msg.canvasGraph.graphSidePanel.pathChecks.pathCheckDeleted,
    });

    fetchBadge();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger on snapshot change
  useEffect(() => {
    fetchBadge();
  }, [ctxProps.snapshotId]);

  return {
    badge,
    currentFilters,
    deletePathLookupCheck,
    fetchBadge,
    fetchPathLookupChecks,
    list,
    listLoadingState,
    loading: badgeLoadingState === 'LOADING',
  };
};
